import React from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

import { Badge, IconCheckFill } from '@fhs/ui';
import { tokens } from '@fhs/ui';

interface Props {
  currentStep: number;
  totalSteps: number;
  style?: StyleProp<ViewStyle>;
  /** When true, omit a11y wrapper (e.g. parent already describes progress in one summary). */
  suppressAccessibilityAnnouncement?: boolean;
}

export function StepsProgress({
  currentStep,
  totalSteps,
  style,
  suppressAccessibilityAnnouncement = false,
}: Props) {
  const isCompleted = (step: number) => step < currentStep;
  const a11yProps = suppressAccessibilityAnnouncement
    ? {
        accessible: false as const,
        accessibilityElementsHidden: true,
        importantForAccessibility: 'no-hide-descendants' as const,
      }
    : {
        accessibilityLabel: `Challenge progress, step ${currentStep} of ${totalSteps}`,
        accessibilityRole: 'text' as const,
        accessible: true as const,
      };

  return (
    <View {...a11yProps} style={[styles.container, style]}>
      <View accessible={false} style={styles.container}>
        {Array.from({ length: totalSteps }).map((_, index) => (
          <React.Fragment key={index}>
            {isCompleted(index) ? (
              <IconCheckFill size={24} color={tokens.colors.$success} />
            ) : (
              <Badge
                circular
                color={tokens.colors.$black10}
                textColor={tokens.colors.$placeholderText}
              >
                {index + 1}
              </Badge>
            )}

            {index < totalSteps - 1 && (
              <View
                style={[
                  styles.separator,
                  {
                    backgroundColor: isCompleted(index + 1)
                      ? tokens.colors.$success
                      : tokens.colors.$black10,
                  },
                ]}
              />
            )}
          </React.Fragment>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  separator: {
    flex: 1,
    height: 2,
    marginHorizontal: 4,
  },
});
